import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import type { CreateDailyMenuRequest, UpdateMenuRequest } from '@/api/types'
import { menusApi } from '@/api/endpoints/menus'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'

/**
 * Create a daily menu (empty / from master / duplicate a past day).
 * A duplicate date throws an ApiError with code 'daily_exists' and
 * data.existingMenuId — the caller opens that day's editor.
 */
export function useCreateDaily() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDailyMenuRequest) => menusApi.createDaily(data),
    onSuccess: (result, variables) => {
      const skipped = result.skipped.length
      let message = `Tagesmenü angelegt (${result.copied} übernommen`
      message += skipped > 0 ? `, ${skipped} übersprungen)` : ')'
      toast.success(message)
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.restaurantId],
      })
    },
    onError: (error) => {
      // 409 daily_exists is handled by the caller (opens the existing day).
      if (error instanceof ApiError && error.code === 'daily_exists') return
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Tagesmenü konnte nicht angelegt werden')
      }
    },
  })
}

/** Update menu metadata (name). */
export function useUpdateMenu() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      data,
    }: {
      menuId: number
      data: UpdateMenuRequest
    }) => menusApi.update(menuId, data),
    onSuccess: (_result, variables) => {
      toast.success('Menü aktualisiert')
      queryClient.invalidateQueries({ queryKey: ['menus', variables.menuId] })
      queryClient.invalidateQueries({ queryKey: ['menus'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Menü konnte nicht aktualisiert werden')
      }
    },
  })
}

/** Delete a daily menu (soft delete). */
export function useDeleteMenu() {
  const queryClient = useQueryClient()
  const { t: tMenus } = useTranslation('menus')

  return useMutation({
    mutationFn: (menuId: number) => menusApi.delete(menuId),
    onSuccess: () => {
      toast.success(tMenus('list.deleteDialog.toastSuccess'))
      queryClient.invalidateQueries({ queryKey: ['menus'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(tMenus('list.deleteDialog.toastFailed'))
      }
    },
  })
}

/** Publish a daily menu (handles 409 missing_prices in the caller). */
export function usePublishMenu() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (menuId: number) => menusApi.publish(menuId),
    onSuccess: (_result, menuId) => {
      toast.success('Menü veröffentlicht')
      queryClient.invalidateQueries({ queryKey: ['menus', menuId] })
      queryClient.invalidateQueries({ queryKey: ['menus'] })
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status !== 409) {
        toast.error(getErrorMessage(error.status, error.code))
      } else if (!(error instanceof ApiError)) {
        toast.error('Menü konnte nicht veröffentlicht werden')
      }
    },
  })
}

/** Unpublish a daily menu. */
export function useUnpublishMenu() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (menuId: number) => menusApi.unpublish(menuId),
    onSuccess: (_result, menuId) => {
      toast.success('Menü offline genommen')
      queryClient.invalidateQueries({ queryKey: ['menus', menuId] })
      queryClient.invalidateQueries({ queryKey: ['menus'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Menü konnte nicht offline genommen werden')
      }
    },
  })
}

/** Generate a POS layout page from a daily menu. */
export function useGeneratePosPage() {
  return useMutation({
    mutationFn: ({
      restaurantId,
      menuId,
    }: {
      restaurantId: number
      menuId: number
    }) => menusApi.generatePosPage(restaurantId, menuId),
    onSuccess: () => {
      toast.success('POS-Seite erzeugt (im Layout ersetzt)')
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('POS-Seite konnte nicht erzeugt werden')
      }
    },
  })
}

/**
 * Ask the server for the link that prints a daily menu's Karte.
 *
 * Read-only, but a mutation rather than a query: it mints a token that expires, so it must run
 * on the click and never be served from cache.
 */
export function usePrintLink() {
  const { t: tMenus } = useTranslation('menus')

  return useMutation({
    mutationFn: (menuId: number) => menusApi.printLink(menuId),
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'url_slug_missing') {
        toast.error(tMenus('editor.menuPreviewSlugMissing'))
        return
      }
      toast.error(tMenus('editor.menuPreviewError'))
    },
  })
}
